import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Ability, Select } from '../../../components';
import { Quote, Change, Project} from '../../../models';
import type { QuoteItem } from '../../../models';
import {
    quoteItemBillingPeriod,
    quoteItemPriceSuffix,
} from '../../../models/Quote';
import { RootState, useAppDispatch, useAppSelector } from '../../../redux';
import { loadQuotes, loadQuote, loadUsers } from '../../../redux';
import { QuoteText, QuoteProgress, Icon, Button, ChangeListItem, Tooltip, QuoteSendModal, QuoteZoneClientSales, CopyButton } from '../../../components';
import { formatPrice, formatMomentDate } from '../../../locales';
import { Badge } from 'flowbite-react';
import QuotesApi from '../../../services/Quotes';

function QuoteView() {
    const { t, i18n } = useTranslation();
    const dispatch    = useAppDispatch();
    const navigate    = useNavigate();

    const { id } = useParams();

    const { quote, quotes, loadingOne } = useAppSelector((state: RootState) => state.quotes);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [pdfError, setPdfError] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);

    useEffect(() => {
        dispatch(loadQuotes());
        dispatch(loadUsers());
    }, [dispatch]);

    useEffect(() => {
        if (id)
            dispatch(loadQuote(id));
    }, [dispatch, id]);

    const goToQuote = (quoteId: string | number) => {
        return navigate(`/quotes/${quoteId}`);
    };

    const downloadPdf = async () => {
        if (!quote?._id || downloadingPdf)
            return;

        setPdfError(false);
        setDownloadingPdf(true);

        try {
            await QuotesApi.downloadPdf(quote._id, `devis-${quote.slug}.pdf`);
        } catch (err) {
            console.error(err);
            setPdfError(true);
        } finally {
            setDownloadingPdf(false);
        }
    };

    if (!quote)
        return null;

    const isLoading = ((loadingOne + '') === 'pending');

    document.title = `Yoda - ${t('changes.quote')} ${quote.name}`;

    return (
        <Ability can="quote:read" redirect="/">
            <div className="QuoteView print:font-netox">
                <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-0 sm:px-6 lg:px-8 print:hidden">
                    <div className="mb-2 mx-auto lg:flex lg:items-center lg:justify-between">
                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0">
                            <h2 className="flex items-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                <Icon type="law" className="inline" size={8} />
                                <Select
                                    onChange={goToQuote}
                                    placeholder={quote?.name}
                                    options={quotes.filter((p: Quote) => p._id !== quote?._id).map((quote: Quote) => ({
                                        value: quote._id,
                                        label: `${quote.slug} | ${quote.name}`,
                                        className: 'text-xl font-bold',
                                        leftContent: (
                                            <Icon type="law" className="inline ml-3 border rounded" size={6} />
                                        )
                                    }))}
                                    inline
                                    className=""
                                    optionsWidth={'w-[30rem]'}
                                    optionsHeight={'max-h-96'}
                                    selectedClassName="text-xl font-bold text-gray-900 dark:text-gray-100"
                                    optionsClassName="w-[29rem] text-sm font-medium py-2 pl-10 truncate"
                                />
                            </h2>
                        </div>
                        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row lg:ml-4 mt-2 lg:mt-0">
                            <Ability can="quote:edit">
                                <span className="sm:ml-3">
                                    <Tooltip content={t('common.edit') || ''} light>
                                        <Button
                                            to={`/quotes/edit/${quote?._id}/`}
                                            title=""
                                            icon="settings"
                                            color="default"
                                        />
                                    </Tooltip>
                                </span>
                            </Ability>
                            <Ability can="quote:edit">
                                <span className="sm:ml-3 print:hidden">
                                    <Button
                                        title={quote.hasBeenSent() ? t('quotes.resend_email') : t('quotes.send_email')}
                                        icon="email"
                                        color="primary"
                                        onClick={() => setShowSendModal(true)}
                                    />
                                </span>
                            </Ability>
                            <span className="sm:ml-3 print:hidden">
                                <Button
                                    title={t('quotes.download_pdf')}
                                    icon="download"
                                    color="secondary"
                                    onClick={downloadPdf}
                                    loading={downloadingPdf}
                                />
                            </span>
                            {quote.signature?.signedAt && (
                                <span className="sm:ml-3 print:hidden">
                                    <Button
                                        title={t('quotes.download_signed_pdf')}
                                        icon="download"
                                        color="success"
                                        onClick={async () => {
                                            if (!quote?._id || downloadingPdf)
                                                return;
                                            setDownloadingPdf(true);
                                            try {
                                                await QuotesApi.downloadPdf(
                                                    quote._id,
                                                    `devis-${quote.slug}-signe.pdf`,
                                                    { signed: true }
                                                );
                                            } catch (err) {
                                                console.error(err);
                                                setPdfError(true);
                                            } finally {
                                                setDownloadingPdf(false);
                                            }
                                        }}
                                        loading={downloadingPdf}
                                    />
                                </span>
                            )}
                            {pdfError && (
                                <span className="sm:ml-3 text-sm text-red-600 print:hidden">
                                    {t('quotes.download_pdf_failed')}
                                </span>
                            )}
                            {quote.publicToken && (
                                <span className="sm:ml-3 print:hidden inline-flex items-center gap-2">
                                    <CopyButton
                                        textToCopy={quote.publicUrl() || ''}
                                        title={t('quotes.public_link') || ''}
                                        hover
                                        size={4}
                                        className="text-sm text-gray-500 dark:text-gray-400"
                                    />
                                    <a
                                        href={quote.publicUrl() || undefined}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={t('quotes.view_public') || ''}
                                        className="text-gray-400 hover:text-gray-700 dark:text-gray-200"
                                    >
                                        <Icon type="eye" size={5} color="gray-500" />
                                    </a>
                                </span>
                            )}
                        </div>
                    </div>
                    {quote.firstViewedAt && (
                        <div className="pb-3 text-sm text-gray-600 dark:text-gray-300 print:hidden">
                            {t('quotes.viewed_at', {
                                date: formatMomentDate(quote.firstViewedAt, 'LLL', i18n.language),
                                count: quote.viewCount || 1,
                            })}
                        </div>
                    )}
                </header>
                <QuoteSendModal
                    show={showSendModal}
                    quote={quote}
                    onClose={() => setShowSendModal(false)}
                    onSent={(updated) => {
                        dispatch(loadQuote(updated._id));
                    }}
                />
                {isLoading ? (
                    <main className="mx-6 mt-2 flex justify-center items-center h-[calc(100vh-8rem)] w-full">
                        <Icon type="loading" size={20} />
                    </main>
                ) : (
                    <main className="mx-6 mt-2">
                        <div className="my-4">
                            <div className="hidden print:block print:mb-8">
                                <QuoteText quote={quote} part="netoxygen" />
                            </div>
                            <div className="flex flex-col-reverse md:flex-row print:flex-row space-y-2 print:space-y-0 md:space-y-0 justify-between items-start">
                                <div className="flex-1 print:flex-auto">
                                    <h3 className="text-lg font-bold text-center md:text-left print:text-left">
                                        {t('quotes.quote_number', {slug: quote.slug})}
                                    </h3>
                                    <h3 className="text-md font-medium text-center md:text-left print:text-left">
                                        {t('quotes.quote_project', {project: quote.project?.name})}
                                    </h3>
                                    {quote.trackedChange?._id && (
                                        <div className="mt-3 print:hidden max-w-md">
                                            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                                {t('quotes.tracked_change')}
                                            </h4>
                                            <ChangeListItem change={quote.trackedChange} showProject />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    {quote.client && (
                                        <div className="mb-2">
                                            <h3 className="text-xl font-bold text-center md:text-right print:text-left">
                                                {quote.client.company}
                                            </h3>
                                            <h3 className="text-md font-bold text-center md:text-right print:text-left">
                                                {quote.client.fullname()}
                                            </h3>
                                            <h3 className="text-sm font-medium text-center md:text-right print:text-left">
                                                {quote.client.address.split(/\\r|\\n/).map((part: string, index: number) => (
                                                    <div key={index}>{part}</div>
                                                ))}
                                            </h3>
                                            <h3 className="text-sm font-medium text-center md:text-right print:text-left">
                                                {quote.client.postcode}&nbsp;{quote.client.city}
                                            </h3>
                                            <h3 className="text-sm font-medium text-center md:text-right print:hidden">
                                                {quote.client.email}
                                            </h3>
                                        </div>
                                    )}
                                    <div className="hidden print:block print:my-4 text-left">
                                        <QuoteText quote={quote} part="date" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 mb-6">
                            <h3 className="text-center text-2xl font-bold print:text-left">
                                {quote.name}
                            </h3>
                            <h3 className="text-center text-xs italic font-normal print:hidden">
                                {t('quotes.last_action')}&nbsp;
                                {quote.lastAction().date
                                    ? formatMomentDate(quote.lastAction().date, 'LL', i18n.language)
                                    : ''}
                            </h3>
                        </div>

                        <div className="my-6">
                            <QuoteText quote={quote} part="intro" />
                        </div>

                        <div className="mb-6 print:hidden">
                            <hr />
                            <h4 className="text-center text-xl font-bold leading-7 text-gray-900 dark:text-gray-100">
                                {t('quotes.total_price', {total: quote.formattedPrice()})}
                            </h4>
                            {quote.hasRecurringFees() && (Number(quote.monthlyPrice) || 0) > 0 && (
                                <h4 className="text-center text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100">
                                    {t('quotes.total_monthly_price', {total: quote.formattedMonthlyPrice()})}
                                </h4>
                            )}
                            {quote.hasRecurringFees() && (Number(quote.quarterlyPrice) || 0) > 0 && (
                                <h4 className="text-center text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100">
                                    {t('quotes.total_quarterly_price', {total: quote.formattedQuarterlyPrice()})}
                                </h4>
                            )}
                            {quote.hasRecurringFees() && (Number(quote.annualPrice) || 0) > 0 && (
                                <h4 className="text-center text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100">
                                    {t('quotes.total_annual_price', {total: quote.formattedAnnualPrice()})}
                                </h4>
                            )}
                            <h4 className="mb-4 text-center font-light leading-7 text-gray-900 dark:text-gray-100">
                                {t('quotes.total_estimated', {total: quote.totalEstimated().toFixed(2)})}
                            </h4>
                            <hr />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-1 gap-2 mt-2 print:break-before-page">
                            <div className="col-span-1 border border-gray-200 dark:border-gray-600 print:border-none p-4 print:p-0 rounded-md">
                                <h3 className="text-xl font-bold mb-2">
                                    {t('quotes.content')}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ">
                                    {quote.items.filter((item: QuoteItem) => item.type !== Change.TYPE_IGNORED).map((item: QuoteItem, i: number) => (
                                        <div key={i} className={`col-span-1 rounded print:rounded-none bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 break-inside-avoid ${item.excluded ? 'opacity-50' : ''}`}>
                                            <div className="grid grid-cols-1 gap-x-2 gap-y-2 p-3">
                                                <div className="col-span-1">
                                                    <div className="flex flex-row gap-x-2 rounded-md border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                                                        <div className="w-32 flex flex-col gap-1">
                                                            <Badge color={Change.color(item.type)} className="w-32">
                                                                {t(`projects.${item.type}`)}
                                                            </Badge>
                                                            {item.removable && !item.quantityMode && (
                                                                <Badge color="warning" className="w-32 justify-center">
                                                                    {t('quotes.removable_item')}
                                                                </Badge>
                                                            )}
                                                            {item.quantityMode && (
                                                                <Badge color="purple" className="w-32 justify-center">
                                                                    {t('quotes.quantity')}: {item.quantity || 1}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <span className="text-md font-medium">
                                                            <div dangerouslySetInnerHTML={{ __html: item.title || ''}}></div>
                                                            <span className="text-sm italic font-normal block">
                                                                <div dangerouslySetInnerHTML={{ __html: item.description || item.change?.localizedDescription(i18n.language) || ''}}></div>
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="col-span-1 flex flex-row gap-2">
                                                    <div className="flex-1 text-left text-gray-900 dark:text-gray-100 font-bold text-sm">
                                                    </div>
                                                    <div className="flex-1 flex flex-col items-end text-right text-gray-900 dark:text-gray-100 font-bold text-sm">
                                                        <div className="flex flex-row gap-x-2 justify-end">
                                                            {item.estimate > 0 && (
                                                                <span>{item.estimate} h</span>
                                                            )}
                                                            {item.price !== 0 && (
                                                                <span>
                                                                    {item.quantityMode
                                                                        ? formatPrice((item.price || 0) * (item.quantity || 1))
                                                                        : formatPrice(item.price)}
                                                                    {quote.hasRecurringFees() ? quoteItemPriceSuffix(item) : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {item.price !== 0 && quote.hasRecurringFees() && (
                                                            <span className="text-xs font-normal text-gray-600 dark:text-gray-300">
                                                                {({
                                                                    oneTime: t('quotes.billing_period_label_one_time'),
                                                                    monthly: t('quotes.billing_period_label_monthly'),
                                                                    quarterly: t('quotes.billing_period_label_quarterly'),
                                                                    annual: t('quotes.billing_period_label_annual'),
                                                                })[quoteItemBillingPeriod(item)]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {item.change && (
                                                <div className="col-span-1 print:hidden" onClick={() => navigate(Project.path(quote.project, 'change', item.change?._id))}>
                                                    <ChangeListItem change={item.change} showProgression />
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-1 gap-2 mt-2 print:break-inside-avoid">
                            <div className="mt-5 print:block print:mt-8 print:break-inside-avoid">
                                <h3 className="text-xl font-bold mb-2">
                                    {t('quotes.pricings_resume')}
                                </h3>
                                <QuoteText quote={quote} part="pricings" />
                            </div>
                            {(quote.sends || []).length > 0 && (
                                <div className="mt-5 col-span-1 flex flex-col space-y-2 print:hidden">
                                    <h3 className="text-xl font-bold">
                                        {t('quotes.send_history')}
                                    </h3>
                                    <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-600 rounded-md space-y-2 text-sm">
                                        {[...(quote.sends || [])].reverse().map((send, index) => (
                                            <div key={index} className="border-b last:border-b-0 border-gray-100 dark:border-gray-700 pb-2 last:pb-0">
                                                <div className="font-medium">
                                                    {send.sentAt
                                                        ? formatMomentDate(send.sentAt, 'LLL', i18n.language)
                                                        : ''}
                                                </div>
                                                <div className="text-gray-600 dark:text-gray-300">
                                                    {t('quotes.send_to')}: {(send.to || []).join(', ')}
                                                    {(send.cc || []).length > 0 && (
                                                        <> · {t('quotes.send_cc')}: {(send.cc || []).join(', ')}</>
                                                    )}
                                                </div>
                                                {send.subject && (
                                                    <div className="text-gray-500 truncate">{send.subject}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <QuoteZoneClientSales quote={quote} />
                            <div className="mt-5 col-span-1 flex flex-col space-y-2 print:hidden print:break-before-page">
                                <h3 className="text-xl font-bold">
                                    {t('quotes.status_progression')}
                                </h3>
                                <div className="bg-white dark:bg-gray-800 p-2 border border-gray-200 dark:border-gray-600 rounded-md">
                                    <QuoteProgress quote={quote} />
                                </div>
                            </div>
                            <div className="hidden print:block print:mt-8 print:break-inside-avoid">
                                <h3 className="text-xl font-bold mb-2">
                                    {t('quotes.conditions')}
                                </h3>
                                <QuoteText quote={quote} part="approve" />
                            </div>
                        </div>
                    </main>
                )}
            </div>
        </Ability>
    );
}

export default QuoteView;
